import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function CategorySection() {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(categories[0].id);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selectedCategory = categories.find((c) => c.id === selected) || categories[0];

  return (
    <section id="categories" className="scroll-mt-20 bg-background-100">
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-foreground-950 md:text-3xl">
            {t('category_section_title')}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-base text-foreground-600">
            {t('category_section_subtitle')}
          </p>
        </div>

        <div className="mx-auto mt-8 flex max-w-xl flex-col items-stretch gap-3 sm:flex-row">
          <div ref={ref} className="relative flex-1">
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="flex w-full items-center justify-between rounded-full border border-background-300 bg-white px-5 py-3 text-left text-sm font-medium text-foreground-900 transition-colors hover:border-primary-400 cursor-pointer"
              aria-haspopup="listbox"
              aria-expanded={open}
            >
              <span>{t(selectedCategory.nameKey)}</span>
              <span className="flex h-4 w-4 items-center justify-center">
                <i className={`text-sm ${open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
              </span>
            </button>

            {open && (
              <div className="absolute z-30 mt-2 max-h-72 w-full overflow-y-auto rounded-xl border border-background-200 bg-white py-1">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => {
                      setSelected(cat.id);
                      setOpen(false);
                    }}
                    className={`flex w-full items-center justify-between px-5 py-2.5 text-left text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      selected === cat.id
                        ? 'bg-primary-100 font-semibold text-primary-700'
                        : 'text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    {t(cat.nameKey)}
                    {selected === cat.id && (
                      <span className="flex h-4 w-4 items-center justify-center">
                        <i className="ri-check-line text-sm"></i>
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <a
            href={selectedCategory.link}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
          >
            {t('category_viewAll')}
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-right-up-line text-sm"></i>
            </span>
          </a>
        </div>

        <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {categories.map((cat) => (
            <a
              key={cat.id}
              href={cat.link}
              target="_blank"
              rel="noopener nofollow"
              className="group overflow-hidden rounded-lg border border-background-200 bg-white transition-transform hover:-translate-y-1 cursor-pointer"
            >
              <div className="h-36 w-full overflow-hidden md:h-44">
                <img
                  src={cat.image}
                  alt={t(cat.nameKey)}
                  title={`USFans ${t(cat.nameKey)}`}
                  className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">
                  {t(cat.nameKey)}
                </span>
                <span className="flex h-5 w-5 items-center justify-center text-primary-500">
                  <i className="ri-arrow-right-line text-base"></i>
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}